import {
  Badge,
  BadgeVariantEnum,
  ChevronUpIcon,
  ClockIcon,
  EditIcon,
  FontColorEnum,
  FontWeightEnum,
  IconButtonColorEnum,
  IconButtonSection,
  IconButtonSectionInternalProps,
  IconButtonSectionSizeEnum,
  IconSizeEnum,
  TextOrientationEnum,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TimeField } from '@/components/DailySchedules/components/TimeField/TimeField';
import { ItemList } from '@/components/ItemList';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { UserAction } from '@/components/UserAction';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { CHANNEL_MEDIA_TYPE_TO_ICON } from '@/features/broadcasters/constants/icons';
import { ChannelRecordingType } from '@/features/channel-recordings/types';
import { useGetScheduleFormattedDates } from '@/hooks/useGetScheduleFormattedDates';
import { getScheduleItemRepetitionDayOfWeekFormatted } from '@/utils/date';

const internalPropsIconButtonEditUser: IconButtonSectionInternalProps = {
  icon: {
    type: EditIcon,
  },
  iconButton: {
    color: IconButtonColorEnum.NONE_DEFAULT,
  },
  text: {
    value: 'Editar',
    orientation: TextOrientationEnum.LEFT,
    className: 'text-text-dark-10 text-body-3 font-regular',
  },
};

type ChannelRecordingDetailsProps = {
  channelRecording: ChannelRecordingType;
  onUpdate: (channelRecording: ChannelRecordingType) => void;
};

export function ChannelRecordingDetail({
  channelRecording,
  onUpdate,
}: ChannelRecordingDetailsProps) {
  const { t } = useTranslation();
  const { data: { data: channels = [] } = {} } = useChannels({
    params: { isParentChannel: false },
  });
  const channel = channelRecording.channel_id
    ? channels.find(channel => channel.id === channelRecording.channel_id)
    : null;
  const ChannelTypeIcon = channel ? CHANNEL_MEDIA_TYPE_TO_ICON[channel.media_type] : null;
  const getScheduleFormattedDates = useGetScheduleFormattedDates();

  const formattedDates = getScheduleFormattedDates({
    startDatetime: channelRecording.start_datetime,
    endDatetime: channelRecording.end_datetime,
    schedule: channelRecording.schedule,
  });
  const hiddenSchedules = formattedDates.schedules.slice(1);
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-1 flex-col items-start self-stretch">
      <div className="flex items-start justify-end self-stretch">
        <IconButtonSection
          onClick={() => onUpdate(channelRecording)}
          internalProps={internalPropsIconButtonEditUser}
          size={IconButtonSectionSizeEnum.LG}
        />
      </div>
      <div className="flex w-full flex-col divide-y divide-stroke-20">
        <ItemList title={t('Emisora')} contentIcon={ChannelTypeIcon}>
          {channel?.name}
        </ItemList>

        <ItemList title={t('Tipo')}>{t('Fichero')}</ItemList>

        <ItemList title={t('Programación')}>
          <button
            className="flex w-full items-center justify-between outline-none"
            type="button"
            onClick={() => {
              setOpen(!open);
            }}
          >
            <div className="flex flex-col gap-1">
              <div>
                <Typography className="text-xs font-semibold text-text-dark-10">
                  {formattedDates.dateRange}
                </Typography>

                {hiddenSchedules.length > 0 ? (
                  <Tooltip
                    help={hiddenSchedules.map((schedule, index) => (
                      <p key={index}>{schedule}</p>
                    ))}
                    grow
                  >
                    <Badge variant={BadgeVariantEnum.DefaultOutline} className="h-[14px]">
                      + {hiddenSchedules.length}
                    </Badge>
                  </Tooltip>
                ) : null}
              </div>

              <Typography className="text-left text-xs text-text-dark-10">
                {formattedDates.schedules[0]}
              </Typography>
            </div>

            <ChevronUpIcon
              className={clsx(
                'mr-size-8 transition-transform duration-500',
                !open ? '-rotate-180' : 'rotate-0'
              )}
            />
          </button>
        </ItemList>

        {open && (
          <div className="flex flex-col gap-2 py-2 pl-6">
            {channelRecording.schedule.map(schedule => (
              <div className="flex w-full">
                <div className="flex w-fit">
                  <ClockIcon className="mt-2 text-text-medium-20" size={IconSizeEnum.SM} />
                </div>

                <div className="flex w-full rounded-size-8 bg-[black]/[.02] p-1">
                  <div className="flex w-1/2 flex-col items-start">
                    <TimeField
                      label={t('Horario')}
                      time={`${schedule.start_hour} - ${schedule.end_hour}`}
                    />
                  </div>
                  {schedule.repetition_day_of_week?.length > 0 ? (
                    <div className="flex w-1/2 items-start gap-1">
                      <Typography color={FontColorEnum.Medium20} weight={FontWeightEnum.Semibold}>
                        {t('Repetición') + ':'}
                      </Typography>
                      <Typography color={FontColorEnum.Medium20}>
                        {getScheduleItemRepetitionDayOfWeekFormatted(schedule)}
                      </Typography>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        <ItemList title={t('Estado')}>
          <StateActiveInTable status={channelRecording.state} />
        </ItemList>

        <ItemList title={t('Creado por')}>
          <UserAction id={channelRecording.created_by} timestamp={channelRecording.created_at} />
        </ItemList>

        <ItemList title={t('Actualizado por')}>
          <UserAction
            id={channelRecording.updated_by ?? channelRecording.created_by}
            timestamp={channelRecording.updated_at ?? channelRecording.created_at}
          />
        </ItemList>
      </div>
    </div>
  );
}
